<template>
  <section class="live-ocean" aria-labelledby="live-ocean-title">
    <div class="live-ocean__inner">
      <h2 id="live-ocean-title">Live Camera Feeds</h2>

      <div class="live-ocean__feature">
        <router-link
          id="live-ocean-panel"
          class="live-ocean__media-link"
          :to="activeDeployment.to"
          :aria-label="activeDeployment.title"
          :aria-labelledby="`live-ocean-tab-${activeIndex}`"
          role="tabpanel"
        >
          <MotionMedia
            :key="activeDeployment.to"
            class="live-ocean__media"
            :src="activeDeployment.src"
            :poster="activeDeployment.poster"
          />
          <span class="live-ocean__shade" aria-hidden="true"></span>
        </router-link>

        <div class="live-ocean__controls" role="tablist" aria-label="Live Camera Feeds">
          <button
            v-for="(deployment, index) in deployments"
            :key="deployment.to"
            type="button"
            :id="`live-ocean-tab-${index}`"
            role="tab"
            aria-controls="live-ocean-panel"
            :aria-selected="index === activeIndex ? 'true' : 'false'"
            :tabindex="index === activeIndex ? 0 : -1"
            :class="{ 'is-active': index === activeIndex }"
            @click="activeIndex = index"
            @keydown="handleControlKeydown($event, index)"
          >
            {{ deployment.title }}
          </button>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import MotionMedia from '@/components/journey/MotionMedia.vue'
import coralCityVideo from '@/assets/videos/coralcity_small.mp4'
import wahooBayVideo from '@/assets/videos/wahoobay.mp4'
import okaloosaVideo from '@/assets/videos/okaloosaislandpier.mp4'
import hawaiiVideo from '@/assets/videos/hawaii_small.mp4'
import coralCityPoster from '@/assets/images/video-posters/coral-city-poster.jpg'
import wahooBayPoster from '@/assets/images/video-posters/wahoo-bay-poster.jpg'
import okaloosaPoster from '@/assets/images/video-posters/okaloosa-island-pier-poster.jpg'
import hawaiiPoster from '@/assets/images/video-posters/mega-lab-hawaii-poster.jpg'

export default {
  name: 'DeploymentSequence',
  components: { MotionMedia },
  data() {
    return {
      activeIndex: 0,
      deployments: [
        { title: 'Coral City Camera', to: '/live-cameras/coral-city', src: coralCityVideo, poster: coralCityPoster },
        { title: 'Wahoo Bay', to: '/live-cameras/wahoo-bay', src: wahooBayVideo, poster: wahooBayPoster },
        { title: 'Okaloosa Island Pier', to: '/live-cameras/okaloosa-island-pier', src: okaloosaVideo, poster: okaloosaPoster },
        { title: 'MEGA Lab Hawaii', to: '/live-cameras/mega-lab-hawaii-1', src: hawaiiVideo, poster: hawaiiPoster },
      ],
    }
  },
  computed: {
    activeDeployment() {
      return this.deployments[this.activeIndex]
    },
  },
  methods: {
    handleControlKeydown(event, index) {
      let nextIndex = index
      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
        nextIndex = (index + 1) % this.deployments.length
      } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
        nextIndex = (index - 1 + this.deployments.length) % this.deployments.length
      } else if (event.key === 'Home') {
        nextIndex = 0
      } else if (event.key === 'End') {
        nextIndex = this.deployments.length - 1
      } else {
        return
      }

      event.preventDefault()
      this.activeIndex = nextIndex
      this.$nextTick(() => {
        const controls = this.$el.querySelectorAll('[role="tab"]')
        if (controls[nextIndex]) controls[nextIndex].focus()
      })
    },
  },
}
</script>

<style lang="scss">
#app .live-ocean {
  padding: clamp(92px, 8vw, 124px) 0 clamp(104px, 9vw, 142px);
  overflow: hidden;
  color: var(--white);
  background: var(--near-black);
}

#app .live-ocean__inner {
  width: min(calc(100% - 80px), var(--page));
  margin: 0 auto;
}

#app .live-ocean h2 {
  max-width: 8ch;
  margin-bottom: clamp(52px, 6vw, 78px);
  font-size: clamp(3.6rem, 7vw, 7.4rem);
  letter-spacing: -0.048em;
}

#app .live-ocean__feature {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(240px, 0.31fr);
  gap: clamp(36px, 5vw, 78px);
  align-items: end;
}

#app .live-ocean__media-link {
  position: relative;
  display: block;
  min-height: min(62svh, 660px);
  overflow: hidden;
  background: #02060e;
  color: var(--white);
}

#app .live-ocean__media,
#app .live-ocean__shade {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}

#app .live-ocean__media {
  object-fit: cover;
  filter: saturate(0.75) contrast(1.05) brightness(0.78);
  transition: filter 300ms ease, transform 800ms cubic-bezier(0.2, 0.7, 0.2, 1);
}

#app .live-ocean__shade {
  background: linear-gradient(180deg, transparent 54%, rgba(3, 9, 20, 0.58));
}

#app .live-ocean__media-link:hover .live-ocean__media,
#app .live-ocean__media-link:focus-visible .live-ocean__media {
  filter: saturate(0.86) contrast(1.05) brightness(0.86);
  transform: scale(1.012);
}

#app .live-ocean__media-link:focus-visible { outline-offset: -4px; }

#app .live-ocean__controls {
  display: grid;
  border-top: 1px solid rgba(207, 237, 248, 0.2);
}

#app .live-ocean__controls button {
  position: relative;
  min-height: 82px;
  padding: 18px 30px 18px 0;
  border: 0;
  border-bottom: 1px solid rgba(207, 237, 248, 0.14);
  color: #9aabb5;
  background: transparent;
  font: inherit;
  font-size: clamp(1.05rem, 1.35vw, 1.28rem);
  line-height: 1.28;
  text-align: left;
  cursor: pointer;
  transition: color 180ms ease, padding-left 180ms ease;
}

#app .live-ocean__controls button::after {
  position: absolute;
  top: 50%;
  right: 2px;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  content: "";
  background: var(--sea-glass);
  opacity: 0;
  transform: translateY(-50%);
}

#app .live-ocean__controls button.is-active {
  padding-left: 14px;
  color: var(--white);
}

#app .live-ocean__controls button.is-active::after { opacity: 1; }

@media (max-width: 1024px) {
  #app .live-ocean__feature { grid-template-columns: 1fr; }
  #app .live-ocean__controls { grid-template-columns: repeat(4, minmax(0, 1fr)); }
  #app .live-ocean__controls button {
    min-height: 96px;
    padding: 18px 18px 18px 0;
    border-right: 1px solid rgba(207, 237, 248, 0.14);
  }
  #app .live-ocean__controls button:last-child { border-right: 0; }
}

@media (max-width: 780px) {
  #app .live-ocean { padding: 88px 0 104px; }
  #app .live-ocean__inner { width: calc(100% - 40px); }
  #app .live-ocean h2 {
    margin-bottom: 52px;
    font-size: clamp(3.2rem, 14vw, 4.8rem);
  }
  #app .live-ocean__media-link {
    min-height: 0;
    aspect-ratio: 4 / 5;
  }
  #app .live-ocean__controls { grid-template-columns: 1fr 1fr; }
  #app .live-ocean__controls button,
  #app .live-ocean__controls button:last-child {
    min-height: 84px;
    padding: 16px 24px 16px 0;
    border-right: 1px solid rgba(207, 237, 248, 0.14);
  }
  #app .live-ocean__controls button:nth-child(even) {
    padding-left: 16px;
    border-right: 0;
  }
  #app .live-ocean__controls button.is-active { padding-left: 10px; }
}

@media (prefers-reduced-motion: reduce) {
  #app .live-ocean__media,
  #app .live-ocean__controls button { transition: none; }
  #app .live-ocean__media-link:hover .live-ocean__media,
  #app .live-ocean__media-link:focus-visible .live-ocean__media { transform: none; }
}
</style>
